import MarginOptions from './MarginOptions'
import Input from './Input'
import RightBarGroup from './RightBarGroup'
import { Button } from '@bm-js/atlascloud-ui'
import Select from './Select'
import {
  ChangeEvent,
  DynamicContentType,
  FileEntryBM,
  FormData,
} from '@bm-js/atlascloud-shared'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

type Props = {
  content: DynamicContentType<FormData>
  setData: (e: ChangeEvent<string | FileEntryBM | undefined>) => void
}

const FormEditor = ({ content, setData }: Props) => {
  const { tr } = useTr()

  const updateField = (e: ChangeEvent, index: number) => {
    const newFields = [...content.data.fields]
    // @ts-expect-error this would be nice to fix
    newFields[index][e.target.name] = e.target.value

    const event = {
      target: {
        name: e.target.name,
        value: newFields,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as ChangeEvent<any>

    setData(event)
  }

  const addField = () => {
    const newFields = [...content.data.fields]
    newFields.push({
      name: tr(TrKey.formEditorNewField),
      type: 'text',
      required: true,
      width: 'half',
    })
    const event = {
      target: {
        name: 'fields',
        value: newFields,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as ChangeEvent<any>
    setData(event)
  }

  const removeField = (index: number) => {
    const newFields = [...content.data.fields]
    newFields.splice(index, 1)
    const event = {
      target: {
        name: 'fields',
        value: newFields,
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as ChangeEvent<any>
    setData(event)
  }

  return (
    <>
      <Input
        light
        type='email'
        name='contactEmail'
        id='contactEmail'
        label={tr(TrKey.formEditorContactEmailLabel)}
        desc={tr(TrKey.formEditorContactEmailDescription)}
        value={content.data.contactEmail}
        onChange={setData}
      />
      <Input
        light
        type='text'
        name='submitText'
        id='submitText'
        label={tr(TrKey.formEditorSubmitTextLabel)}
        desc={tr(TrKey.formEditorSubmitTextDescription)}
        value={content.data.submitText}
        onChange={setData}
      />
      <Input
        light
        type='textarea'
        name='successText'
        id='successText'
        label={tr(TrKey.formEditorSuccessTextLabel)}
        desc={tr(TrKey.formEditorSuccessTextDescription)}
        value={content.data.successText}
        onChange={setData}
      />
      <RightBarGroup title='Bekräftelsemail' topLine>
        <Input
          light
          type='text'
          name='senderConfirmationEmailSubject'
          id='senderConfirmationEmailSubject'
          label={tr(TrKey.formEditorSenderConfirmationEmailSubjectLabel)}
          desc={tr(TrKey.formEditorSenderConfirmationEmailSubjectDescription)}
          value={content.data.senderConfirmationEmailSubject}
          onChange={setData}
        />
        <Input
          light
          type='rich-text'
          name='senderConfirmationEmailBody'
          id='senderConfirmationEmailBody'
          label={tr(TrKey.formEditorSenderConfirmationEmailBodyLabel)}
          desc={tr(TrKey.formEditorSenderConfirmationEmailBodyDescription)}
          value={content.data.senderConfirmationEmailBody}
          onChange={setData}
        />
        <Input
          light
          type='image'
          label={tr(TrKey.formEditorSenderConfirmationEmailImageLabel)}
          desc={tr(TrKey.formEditorSenderConfirmationEmailImageDescription)}
          value={content.data.senderConfirmationEmailImage}
          setter={(f) =>
            setData({ target: { name: 'senderConfirmationEmailImage', value: f } })
          }
          deleteMedia={() =>
            setData({ target: { name: 'senderConfirmationEmailImage', value: undefined } })
          }
        />
      </RightBarGroup>
      <RightBarGroup title={tr(TrKey.formEditorFieldsTitle)}>
        {content.data.fields.map((field, index) => (
          <div key={`form_editor_field_${index}`}>
            <Input
              light
              noMargin
              type='text'
              name='name'
              id='name'
              label={tr(TrKey.formEditorFieldNameLabel)}
              value={field.name}
              onChange={(e) => updateField(e, index)}
            />
            <div className='mb-2 mt-2 flex items-start justify-between'>
              <Input
                type='checkbox'
                light
                noMargin
                label={tr(TrKey.formEditorRequiredLabel)}
                name='required'
                value={field.required}
                onChange={(e) => updateField(e, index)}
                id={`required_${index}`}
              />
              <Button danger sm onClick={() => removeField(index)}>
                {tr(TrKey.remove)}
              </Button>
            </div>
            <div className='mb-2 mt-2 flex items-start gap-2'>
              <Select
                light
                noMargin
                value={field.type}
                name='type'
                onChange={(e) => updateField(e, index)}
              >
                <option value='' disabled>
                  {tr(TrKey.formEditorFieldTypePlaceholder)}
                </option>
                <option value='text'>
                  {tr(TrKey.formEditorFieldTypeOptionText)}
                </option>
                <option value='textarea'>
                  {tr(TrKey.formEditorFieldTypeOptionTextarea)}
                </option>
                <option value='email'>
                  {tr(TrKey.formEditorFieldTypeOptionEmail)}
                </option>
                <option value='tel'>
                  {tr(TrKey.formEditorFieldTypeOptionTel)}
                </option>
              </Select>
              <Select
                light
                noMargin
                value={field.width}
                name='width'
                onChange={(e) => updateField(e, index)}
              >
                <option value='' disabled>
                  {tr(TrKey.formEditorFieldWidthPlaceholder)}
                </option>
                <option value='full'>
                  {tr(TrKey.formEditorFieldWidthOptionFull)}
                </option>
                <option value='half'>
                  {tr(TrKey.formEditorFieldWidthOptionHalf)}
                </option>
              </Select>
            </div>
            <div className='mb-4 h-px bg-neutral-400' />
          </div>
        ))}
        <Button onClick={addField}>{tr(TrKey.formEditorAddField)}</Button>
      </RightBarGroup>
      <MarginOptions content={content} setData={setData} />
    </>
  )
}

export default FormEditor
